import React, { useState } from 'react';
import './Humanizetext.css';
import { Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Link } from 'react-router-dom';

function Humanizetext() {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Helper function to get random items from an array with unique combinations
    const getRandomItems = (array, count = 1) => {
        const shuffled = [...array].sort(() => 0.5 - Math.random());
        return count === 1 ? shuffled[0] : shuffled.slice(0, count);
    };

    // Helper function for controlled randomization
    const shouldApply = (probability = 0.5, context = {}) => {
        const baseProb = probability * (context.importance || 1);
        return Math.random() < baseProb;
    };

    const humanizeText = (text) => {
        // Initial text preprocessing
        let processedText = text.trim()
            // Remove common AI markers
            .replace(/\b(?:clearly|obviously|evidently|notably|significantly|essentially|fundamentally)\b/gi, '')
            .replace(/\b(?:it is worth noting|it should be noted|it is important to note|it is interesting to note)\b/gi, '')
            .replace(/\b(?:in conclusion|to conclude|to summarize|in summary|in essence|in brief)\b/gi, '')
            .replace(/\b(?:firstly|secondly|thirdly|lastly|finally|ultimately)\b/gi, '')
            .replace(/\b(?:due to the fact that|in light of|with respect to|in regard to)\b/gi, 'because')
            .replace(/\b(?:in order to|for the purpose of|with the intention of)\b/gi, 'to')
            .replace(/\b(?:utilize|implement|leverage|facilitate|optimize)\b/gi, 'use')
            .replace(/\b(?:demonstrate|illustrate|exemplify)\b/gi, 'show');

        // Enhanced writing patterns with deep variations
        const patterns = {
            formalToInformal: {
                'therefore': ['so', 'that\'s why', 'which means', 'this means', 'and because of that', 'that leads to'],
                'however': ['but', 'though', 'still', 'yet', 'although', 'even though', 'mind you', 'that said'],
                'additionally': ['also', 'plus', 'on top of that', 'not to mention', 'and another thing', 'adding to that'],
                'consequently': ['so', 'because of this', 'that\'s why', 'which led to', 'this resulted in', 'as a result'],
                'furthermore': ['also', 'plus', 'what\'s more', 'adding to that', 'not only that', 'besides that'],
                'nevertheless': ['still', 'even so', 'but still', 'despite that', 'regardless', 'anyway'],
                'subsequently': ['later', 'after that', 'then', 'next', 'following that', 'after this'],
                'moreover': ['plus', 'also', 'on top of that', 'what\'s more', 'not to mention', 'another thing'],
                'in addition': ['also', 'plus', 'another thing is', 'and then there\'s', 'oh, and', 'by the way'],
                'for example': ['like', 'say', 'let\'s say', 'imagine', 'think about', 'take']
            },
            conversationalStarters: [
                'You see', 'Look', 'Here\'s the thing', 'The thing is', 'I mean', 'Well',
                'Okay so', 'Right, so', 'So basically', 'Get this', 'Check this out',
                'Here\'s what I think', 'You know what', 'Let me tell you', 'Listen'
            ],
            midSentenceConnectors: [
                'like', 'you know', 'I mean', 'kind of', 'sort of', 'basically',
                'pretty much', 'more or less', 'in a way', 'somewhat', 'apparently',
                'from what I can tell', 'as far as I know', 'if you ask me'
            ],
            emotiveExpressions: [
                'honestly', 'seriously', 'surprisingly', 'interestingly',
                'amazingly', 'unfortunately', 'thankfully', 'luckily',
                'hopefully', 'obviously', 'naturally', 'clearly'
            ],
            casualPhrases: [
                'pretty', 'quite', 'rather', 'fairly', 'somewhat',
                'kind of', 'sort of', 'more or less', 'basically',
                'generally', 'usually', 'typically', 'normally'
            ],
            personalOpeners: [
                'I think', 'I believe', 'I feel like', 'In my opinion',
                'From my experience', 'As far as I can tell',
                'From what I\'ve seen', 'Based on what I know'
            ],
            emphasis: [
                'really', 'very', 'totally', 'absolutely', 'completely',
                'seriously', 'literally', 'definitely', 'actually', 'genuinely'
            ],
            hedging: [
                'maybe', 'probably', 'possibly', 'perhaps', 'seemingly',
                'apparently', 'likely', 'potentially', 'presumably', 'supposedly'
            ]
        };

        // Advanced sentence structure variations
        const sentencePatterns = [
            (s) => `${getRandomItems(patterns.conversationalStarters)}, ${s.toLowerCase()}`,
            (s) => `${getRandomItems(patterns.personalOpeners)}, ${s.toLowerCase()}`,
            (s) => `${getRandomItems(patterns.emotiveExpressions)}, ${s.toLowerCase()}`,
            (s) => s.replace(/^/, `${getRandomItems(patterns.hedging)}, `),
            (s) => s.replace(/,\s+/g, `, ${getRandomItems(patterns.midSentenceConnectors)}, `),
            (s) => s.replace(/\./g, `... ${getRandomItems(patterns.casualPhrases)}...`),
            (s) => s.replace(/([.!?])$/, `${getRandomItems(patterns.emphasis)}$1`)
        ];

        // Split into paragraphs while preserving structure
        const paragraphs = processedText.split('\n\n').map(para => para.trim());
        
        let humanizedParagraphs = paragraphs.map((paragraph, paraIndex) => {
            // Split into sentences while preserving punctuation
            let sentences = paragraph.split(/(?<=[.!?])\s+/);
            
            // Process each sentence with deep humanization
            sentences = sentences.map((sentence, index) => {
                if (!sentence.trim()) return '';

                // Apply multiple humanization layers in a single pass
                let humanizedSentence = sentence;

                // 1. Structure Variation
                if (shouldApply(0.7)) {
                    humanizedSentence = getRandomItems(sentencePatterns)(humanizedSentence);
                }

                // 2. Informal Replacements
                Object.entries(patterns.formalToInformal).forEach(([formal, informal]) => {
                    if (shouldApply(0.8)) {
                        const replacement = getRandomItems(informal);
                        humanizedSentence = humanizedSentence.replace(
                            new RegExp(`\\b${formal}\\b`, 'gi'),
                            replacement
                        );
                    }
                });

                // 3. Natural Language Elements
                if (shouldApply(0.75)) {
                    // Add mid-sentence connectors
                    humanizedSentence = humanizedSentence.replace(
                        /,\s+/g,
                        `, ${getRandomItems(patterns.midSentenceConnectors)}, `
                    );
                }

                // 4. Emphasis and Hedging
                if (shouldApply(0.6)) {
                    const emphasisWord = getRandomItems(patterns.emphasis);
                    const position = Math.floor(Math.random() * humanizedSentence.split(' ').length);
                    const words = humanizedSentence.split(' ');
                    words.splice(position, 0, emphasisWord);
                    humanizedSentence = words.join(' ');
                }

                // 5. Natural Typos and Corrections (very subtle)
                if (shouldApply(0.15)) {
                    const words = humanizedSentence.split(' ');
                    const randomIndex = Math.floor(Math.random() * words.length);
                    const word = words[randomIndex];
                    if (word.length > 4) {
                        const typoVariations = [
                            () => `${word.slice(0, -1)}${word.slice(-1)}`, // Double letter
                            () => `${word} (${getRandomItems(['I mean', 'sorry', 'correction', '*'])} ${word})`, // Self-correction
                            () => word.replace(/([aeiou])/i, '$1$1'), // Doubled vowel
                        ];
                        words[randomIndex] = getRandomItems(typoVariations)();
                        humanizedSentence = words.join(' ');
                    }
                }

                // 6. Punctuation Variation
                if (shouldApply(0.65)) {
                    humanizedSentence = humanizedSentence
                        .replace(/\./g, shouldApply(0.5) ? '...' : '!')
                        .replace(/([.!?])$/, match => shouldApply(0.3) ? `${match}!` : match);
                }

                return humanizedSentence;
            });

            // Join sentences with natural flow
            let processedParagraph = sentences.join(' ').trim();

            // Add paragraph-level variations
            if (shouldApply(0.8) && paraIndex === 0) {
                const opener = getRandomItems(patterns.personalOpeners);
                processedParagraph = `${opener}, ${processedParagraph.charAt(0).toLowerCase() + processedParagraph.slice(1)}`;
            }

            return processedParagraph;
        });

        // Final processing with deep humanization
        let finalText = humanizedParagraphs.join('\n\n');

        // Enhanced contractions with context awareness
        const contractions = {
            'it is': 'it\'s',
            'I am': 'I\'m',
            'you are': 'you\'re',
            'they are': 'they\'re',
            'we are': 'we\'re',
            'that is': 'that\'s',
            'what is': 'what\'s',
            'who is': 'who\'s',
            'where is': 'where\'s',
            'how is': 'how\'s',
            'is not': 'isn\'t',
            'are not': 'aren\'t',
            'was not': 'wasn\'t',
            'were not': 'weren\'t',
            'have not': 'haven\'t',
            'has not': 'hasn\'t',
            'will not': 'won\'t',
            'would not': 'wouldn\'t',
            'could not': 'couldn\'t',
            'should not': 'shouldn\'t',
            'does not': 'doesn\'t',
            'do not': 'don\'t',
            'did not': 'didn\'t',
            'cannot': 'can\'t',
            'I will': 'I\'ll',
            'you will': 'you\'ll',
            'he will': 'he\'ll',
            'she will': 'she\'ll',
            'they will': 'they\'ll',
            'we will': 'we\'ll'
        };

        // Apply contractions with natural variation
        Object.entries(contractions).forEach(([full, contracted]) => {
            if (shouldApply(0.85)) {
                finalText = finalText.replace(new RegExp(`\\b${full}\\b`, 'gi'), contracted);
            }
        });

        // Final natural language enhancement
        finalText = finalText
            // Transform academic phrases
            .replace(/\b(?:according to|based on|studies show|research indicates)\b/gi,
                    () => shouldApply(0.5) ? 'from what I\'ve seen' : 'I\'ve found that')
            .replace(/\b(?:it is crucial|it is essential|it is vital)\b/gi,
                    () => shouldApply(0.5) ? 'it\'s really important' : 'we need to')
            .replace(/\b(?:numerous|multiple|various)\b/gi,
                    () => shouldApply(0.5) ? 'many' : 'lots of')
            // Add natural redundancy
            .replace(/\b(really|very|quite|pretty)\b/gi,
                    match => shouldApply(0.3) ? `${match} ${match}` : match)
            // Break formal patterns
            .replace(/\b(?:thus|hence|thereby|wherein|whereby)\b/gi, 'so')
            .replace(/\b(?:whilst|among|amongst|amidst)\b/gi, 'while')
            .replace(/\b(?:utilize|employ|implement)\b/gi, 'use')
            .replace(/\b(?:regarding|concerning|pertaining to)\b/gi, 'about');

        return finalText;
    };

    const handleConvert = () => {
        if (!inputText.trim()) return;
        setIsLoading(true);
        
        // Add a realistic delay for processing
        setTimeout(() => {
            try {
                const humanized = humanizeText(inputText);
                setOutputText(humanized);
            } catch (error) {
                console.error('Error humanizing text:', error);
                setOutputText('Error processing text. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }, 1500);
    };

    const handleCopy = () => {
        if (outputText) {
            navigator.clipboard.writeText(outputText);
        }
    };

    return (
        <Box className="chatseaction1">
            <div className="page-container">
                <div className="page-content">
                    <div className="header">
                        <Link to="/" style={{ textDecoration: 'none' }}>
                            <button className="back-link">
                                <ArrowBackIcon className="back-icon" />
                                Back to Chat
                            </button>
                        </Link>
                        <div className="header-content">
                            <h1>Humanize Text</h1>
                            <p>Transform AI-generated content into natural human writing</p>
                        </div>
                        {/* <Darkmode /> */}
                    </div>

                    <Box className="main-section">
                        <Box className="column">
                            <h2>Original Text</h2>
                            <Box className="text-box-container">
                                <textarea
                                    className="text-box"
                                    placeholder="Paste your AI-generated text here..."
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                />
                            </Box>
                            <button 
                                className={`action-btn convert-btn ${!inputText.trim() ? 'disabled' : ''}`}
                                onClick={handleConvert}
                                disabled={!inputText.trim() || isLoading}
                            >
                                {isLoading ? (
                                    <span className="loading-dots">Converting<span>.</span><span>.</span><span>.</span></span>
                                ) : (
                                    'Convert'
                                )}
                            </button>
                        </Box>

                        <Box className="column">
                            <h2>Humanized Text</h2>
                            <Box className="text-box-container">
                                <textarea
                                    className="text-box"
                                    placeholder="Your humanized text will appear here..."
                                    value={outputText}
                                    readOnly
                                />
                            </Box>
                            <button 
                                className={`action-btn copy-btn ${!outputText ? 'disabled' : ''}`}
                                onClick={handleCopy}
                                disabled={!outputText}
                            >
                                <ContentCopyIcon className="copy-icon" />
                                Copy
                            </button>
                        </Box>
                    </Box>
                </div>
            </div>
        </Box>
    );
}

export default Humanizetext;
